/* API products, organisations, apps, credentials and product grants -- the console side of
 * the Apigee-aligned access model (docs/product-access-design.html).
 *
 *   AccessProductsPage   API products: bundles of API operations an organisation is granted
 *   AccessOrgsPage       organisations (Apigee AppGroups), members, keys (credentials), grants;
 *                        pending grants are counted on the list and approved on the credential
 *
 * Per-customer usage is the Grafana "API Consumers" dashboard (Observability -> Metrics ->
 * Consumers); there is no separate usage page.
 *
 * Server rules live in SecurityConfig: reads need any Axon role; product changes need
 * platform-admin or api-product-owner; everything that hands out access needs
 * platform-admin or api-approver. A client secret is shown exactly once, here, on creation
 * or rotation (AccessSecretModal), and never stored by Axon.
 *
 * Shares the global scope with the other SPA scripts: every top-level name is prefixed
 * Access / ACCESS_, and the React hook destructure is a loose var like everywhere else. */
var {useState,useEffect,useCallback,useMemo}=React;

const ACCESS_LIFECYCLE_BADGE={DRAFT:'gray',PUBLISHED:'success',DEPRECATED:'warning',RETIRED:'error'};
const ACCESS_STATUS_BADGE={APPROVED:'success',ACTIVE:'success',PENDING:'warning',REVOKED:'error',REJECTED:'error',INACTIVE:'gray'};
const ACCESS_UNITS=['SECOND','MINUTE','HOUR','DAY','MONTH'];

function AccessBadge({value}){
  return <span className={'badge badge-'+(ACCESS_STATUS_BADGE[value]||ACCESS_LIFECYCLE_BADGE[value]||'gray')} style={{fontSize:10}}>{value||'—'}</span>;
}

function AccessModal({title,onClose,children,wide}){
  return(
    <div className="modal-overlay" onClick={onClose}>
      <div className={'modal'+(wide?'':' modal-sm')} style={wide?{maxWidth:820,width:'92vw'}:null} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-heading"><div style={{fontWeight:600,fontSize:15}}>{title}</div></div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{maxHeight:'72vh',overflow:'auto'}}>{children}</div>
      </div>
    </div>
  );
}

/** Shows a client secret once. Closing it is the last time anyone sees the secret. */
function AccessSecretModal({payload,onClose}){
  const [copied,setCopied]=useState('');
  if(!payload)return null;
  const copy=async(label,text)=>{
    try{await navigator.clipboard.writeText(text);setCopied(label);}catch(e){setCopied('');}
  };
  return(
    <AccessModal title="Credential issued" onClose={onClose}>
      <p style={{marginTop:0,color:'var(--error)',fontWeight:600}}>{payload.warning}</p>
      {[['Client ID',payload.clientId],['Client secret',payload.clientSecret]].map(([label,val])=>(
        <div key={label} className="form-group">
          <label className="form-label">{label}</label>
          <div style={{display:'flex',gap:8}}>
            <input className="form-input font-mono" readOnly value={val||''} onFocus={e=>e.target.select()} id={'access-secret-'+label.replace(/\s/g,'')}/>
            <button className="btn btn-sm btn-ghost" onClick={()=>copy(label,val)}>{copied===label?'Copied':'Copy'}</button>
          </div>
        </div>
      ))}
      <p className="text-sm text-muted">Token endpoint: <code>POST /api/external-auth/token</code> with <code>grant_type=client_credentials</code>.</p>
      <div style={{textAlign:'right'}}><button className="btn btn-primary" onClick={onClose}>I have stored it</button></div>
    </AccessModal>
  );
}

function accessQuotaText(q){
  if(!q||!q.enabled)return 'No quota';
  return q.limit.toLocaleString()+' / '+(q.interval>1?q.interval+' ':'')+String(q.unit||'DAY').toLowerCase();
}

// ── API products ───────────────────────────────────────────────────────────

function AccessProductEditor({initial,apis,onSave,onClose}){
  const blank={id:'',displayName:'',description:'',access:'PUBLIC',approvalType:'MANUAL',environments:['sandbox'],
    operations:[],quota:{limit:10000,interval:1,unit:'DAY',enabled:true},quotaCounterScope:'PRODUCT',scopes:[],attributes:{}};
  const [p,setP]=useState(()=>initial?JSON.parse(JSON.stringify(initial)):blank);
  const [members,setMembers]=useState({});   // apiId -> [{integrationId, integrationName, publicPath}]
  const [err,setErr]=useState('');
  const isNew=!initial;
  const set=(k,v)=>setP(o=>Object.assign({},o,{[k]:v}));
  const quota=p.quota||{limit:0,interval:1,unit:'DAY',enabled:false};
  const setQuota=(k,v)=>set('quota',Object.assign({},quota,{[k]:v}));

  const loadMembers=useCallback(async(apiId)=>{
    if(!apiId||members[apiId])return;
    try{const d=await API('GET','/api/admin/api-products/'+apiId);
      setMembers(m=>Object.assign({},m,{[apiId]:(d.members||d.product&&d.product.members||[])}));}
    catch(e){}
  },[members]);
  useEffect(()=>{(p.operations||[]).forEach(o=>loadMembers(o.apiId));},[p.operations,loadMembers]);

  const setOp=(i,patch)=>set('operations',p.operations.map((o,j)=>j===i?Object.assign({},o,patch):o));
  const addOp=()=>{const free=apis.find(a=>!p.operations.some(o=>o.apiId===a.id));
    if(free)set('operations',p.operations.concat([{apiId:free.id,memberIds:[]}]));};

  const save=()=>{
    setErr('');
    if(isNew&&!/^[a-z0-9][a-z0-9-]{1,62}$/.test(p.id||'')){setErr('ID: 2-63 lower-case letters, digits or "-".');return;}
    if(!(p.displayName||'').trim()){setErr('A display name is required.');return;}
    for(const o of p.operations){if(!o.apiId){setErr('Every operation row needs an API.');return;}}
    onSave(p).catch(e=>setErr(e.message));
  };

  return(
    <AccessModal title={isNew?'New API product':'Edit '+(initial.displayName||initial.id)} onClose={onClose} wide>
      {err&&<div className="alert alert-error" style={{marginBottom:12}}>{err}</div>}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
        <div className="form-group"><label className="form-label" htmlFor="ap-id">ID</label>
          <input id="ap-id" className="form-input font-mono" disabled={!isNew} value={p.id} placeholder="jurist-standard"
                 onChange={e=>set('id',e.target.value.toLowerCase())}/></div>
        <div className="form-group"><label className="form-label" htmlFor="ap-name">Display name</label>
          <input id="ap-name" className="form-input" value={p.displayName||''} placeholder="Jurist Standard" onChange={e=>set('displayName',e.target.value)}/></div>
      </div>
      <div className="form-group"><label className="form-label" htmlFor="ap-desc">Description</label>
        <textarea id="ap-desc" className="form-input" rows={2} value={p.description||''} onChange={e=>set('description',e.target.value)}/></div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
        <div className="form-group"><label className="form-label" htmlFor="ap-access">Access</label>
          <select id="ap-access" className="form-input" value={p.access} onChange={e=>set('access',e.target.value)}>
            <option value="PUBLIC">Public: in the portal catalogue</option>
            <option value="PRIVATE">Private: granted by staff only</option>
            <option value="INTERNAL">Internal: TaxLab apps, staff only</option>
          </select></div>
        <div className="form-group"><label className="form-label" htmlFor="ap-appr">Approval</label>
          <select id="ap-appr" className="form-input" value={p.approvalType} onChange={e=>set('approvalType',e.target.value)}>
            <option value="MANUAL">Manual: an approver decides</option>
            <option value="AUTO">Automatic</option>
          </select></div>
        <div className="form-group"><label className="form-label" htmlFor="ap-env">Environments</label>
          <input id="ap-env" className="form-input" value={(p.environments||[]).join(', ')}
                 onChange={e=>set('environments',e.target.value.split(',').map(s=>s.trim()).filter(Boolean))}/></div>
      </div>

      <div className="form-label" style={{marginTop:6}}>APIs and operations</div>
      {(p.operations||[]).length===0&&<div className="text-sm text-muted" style={{marginBottom:8}}>No APIs yet. A published product needs at least one.</div>}
      {(p.operations||[]).map((o,i)=>{
        const all=!o.memberIds||o.memberIds.length===0;
        const ms=members[o.apiId]||[];
        return(
          <div key={i} className="card" style={{marginBottom:8}}><div className="card-body" style={{padding:12}}>
            <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
              <select className="form-input" style={{maxWidth:320}} value={o.apiId} aria-label="API"
                      onChange={e=>{setOp(i,{apiId:e.target.value,memberIds:[]});loadMembers(e.target.value);}}>
                {apis.map(a=><option key={a.id} value={a.id}>{a.name} ({a.apiVersion||a.version||a.id})</option>)}
              </select>
              <label className="text-sm" style={{display:'flex',gap:6,alignItems:'center'}}>
                <input type="checkbox" checked={all} onChange={e=>setOp(i,{memberIds:e.target.checked?[]:ms.map(m=>m.integrationId)})}/>
                All operations, including ones added later
              </label>
              <div style={{flex:1}}/>
              <button className="btn btn-ghost btn-sm" onClick={()=>set('operations',p.operations.filter((_,j)=>j!==i))}>Remove</button>
            </div>
            {!all&&(
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:4,marginTop:8}}>
                {ms.map(m=>(
                  <label key={m.integrationId} className="text-sm" style={{display:'flex',gap:6,alignItems:'center'}}>
                    <input type="checkbox" checked={o.memberIds.includes(m.integrationId)}
                           onChange={e=>setOp(i,{memberIds:e.target.checked?o.memberIds.concat([m.integrationId]):o.memberIds.filter(x=>x!==m.integrationId)})}/>
                    <span>{m.integrationName||m.integrationId} <span className="font-mono text-muted" style={{fontSize:11}}>{m.publicPath}</span></span>
                  </label>
                ))}
              </div>
            )}
          </div></div>
        );
      })}
      <button className="btn btn-ghost btn-sm" onClick={addOp} disabled={apis.every(a=>p.operations.some(o=>o.apiId===a.id))}>+ Add API</button>

      <div className="form-label" style={{marginTop:14}}>Quota (per app, per product)</div>
      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
        <label className="text-sm" style={{display:'flex',gap:6,alignItems:'center'}}>
          <input type="checkbox" checked={!!quota.enabled} onChange={e=>setQuota('enabled',e.target.checked)}/> Enforce
        </label>
        <input className="form-input" type="number" min="1" style={{width:130}} aria-label="Quota limit" value={quota.limit}
               onChange={e=>setQuota('limit',parseInt(e.target.value||'0',10))}/>
        <span className="text-sm">calls per</span>
        <input className="form-input" type="number" min="1" style={{width:80}} aria-label="Quota interval" value={quota.interval}
               onChange={e=>setQuota('interval',parseInt(e.target.value||'1',10))}/>
        <select className="form-input" style={{width:130}} aria-label="Quota unit" value={quota.unit} onChange={e=>setQuota('unit',e.target.value)}>
          {ACCESS_UNITS.map(u=><option key={u} value={u}>{u.toLowerCase()}</option>)}
        </select>
        <select className="form-input" style={{width:260}} aria-label="Counter" value={p.quotaCounterScope} onChange={e=>set('quotaCounterScope',e.target.value)}>
          <option value="PRODUCT">One counter across the product</option>
          <option value="API">A counter per API</option>
          <option value="OPERATION">A counter per operation</option>
        </select>
      </div>
      <div className="form-group" style={{marginTop:12}}><label className="form-label" htmlFor="ap-scopes">Required token scopes (optional)</label>
        <input id="ap-scopes" className="form-input" value={(p.scopes||[]).join(' ')} placeholder="e.g. invoices:write"
               onChange={e=>set('scopes',e.target.value.split(/[\s,]+/).filter(Boolean))}/>
        <div className="text-sm text-muted">When set, a token must carry at least one of these scopes.</div></div>
      <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:12}}>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save}>{isNew?'Create as draft':'Save'}</button>
      </div>
    </AccessModal>
  );
}

function AccessProductsPage({showToast,confirm}){
  const [products,setProducts]=useState([]);
  const [apis,setApis]=useState([]);
  const [loading,setLoading]=useState(true);
  const [editing,setEditing]=useState(null);   // null | 'new' | product

  const reload=useCallback(async()=>{
    setLoading(true);
    try{
      const [ps,as]=await Promise.all([API('GET','/api/admin/products'),API('GET','/api/admin/api-products')]);
      setProducts(Array.isArray(ps)?ps:[]);
      setApis(Array.isArray(as)?as:[]);
    }catch(e){showToast&&showToast('Could not load API products: '+e.message,'error');}
    finally{setLoading(false);}
  },[showToast]);
  useEffect(()=>{reload();},[reload]);

  const apiName=id=>(apis.find(a=>a.id===id)||{}).name||id;
  const save=async(p)=>{
    if(editing==='new')await API('POST','/api/admin/products',p);
    else await API('PUT','/api/admin/products/'+p.id,p);
    showToast&&showToast(editing==='new'?'API product created as a draft':'API product saved','success');
    setEditing(null);
    await reload();
  };
  const lifecycle=async(p,to)=>{
    try{await API('PUT','/api/admin/products/'+p.id+'/lifecycle',{lifecycle:to});
      showToast&&showToast(p.displayName+' is now '+to.toLowerCase(),'success');await reload();}
    catch(e){showToast&&showToast(e.message,'error');}
  };
  const remove=async(p)=>{
    const ok=confirm?await confirm('Delete API product "'+p.displayName+'"? Credentials that still hold it must be revoked first.'):false;
    if(!ok)return;
    try{await API('DELETE','/api/admin/products/'+p.id);showToast&&showToast('API product deleted','success');await reload();}
    catch(e){showToast&&showToast(e.message,'error');}
  };

  return(
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">API Products</h1>
          <div className="page-subtitle">What organisations are granted: bundles of API operations with access level, approval and quota</div>
        </div>
        <button className="btn btn-primary" style={typeof TAXLAB_CTA_STYLE!=='undefined'?TAXLAB_CTA_STYLE:null} onClick={()=>setEditing('new')}>New API product</button>
      </div>
      {loading&&<div className="card"><div className="card-body">Loading…</div></div>}
      {!loading&&products.length===0&&(
        <div className="card"><div className="card-body" style={{textAlign:'center',padding:40}}>
          <h3 style={{margin:'0 0 8px'}}>No API products yet</h3>
          <p className="text-muted" style={{margin:0}}>An API product bundles operations of one or more APIs. Organisations are granted products, never individual APIs.</p>
        </div></div>
      )}
      {!loading&&products.length>0&&(
        <div className="card"><div className="card-body" style={{padding:0,overflowX:'auto'}}>
          <table className="table" style={{width:'100%'}}>
            <thead><tr><th>API product</th><th>Access</th><th>Approval</th><th>Lifecycle</th><th>APIs</th><th>Quota</th><th></th></tr></thead>
            <tbody>
              {products.map(p=>(
                <tr key={p.id}>
                  <td><div style={{fontWeight:600}}>{p.displayName}</div><div className="font-mono text-muted" style={{fontSize:11}}>{p.id}</div></td>
                  <td>{(p.access||'').toLowerCase()}</td>
                  <td>{(p.approvalType||'').toLowerCase()}</td>
                  <td><AccessBadge value={p.lifecycle}/></td>
                  <td style={{fontSize:12}}>{(p.operations||[]).map(o=><div key={o.apiId}>{apiName(o.apiId)}{o.memberIds&&o.memberIds.length?' ('+o.memberIds.length+' ops)':''}</div>)}</td>
                  <td style={{fontSize:12}}>{accessQuotaText(p.quota)}<div className="text-muted">{(p.quotaCounterScope||'PRODUCT').toLowerCase()} counter</div></td>
                  <td style={{whiteSpace:'nowrap',textAlign:'right'}}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setEditing(p)}>Edit</button>
                    {p.lifecycle==='DRAFT'&&<button className="btn btn-ghost btn-sm" onClick={()=>lifecycle(p,'PUBLISHED')}>Publish</button>}
                    {p.lifecycle==='PUBLISHED'&&<button className="btn btn-ghost btn-sm" onClick={()=>lifecycle(p,'DEPRECATED')}>Deprecate</button>}
                    {p.lifecycle==='DEPRECATED'&&<button className="btn btn-ghost btn-sm" onClick={()=>lifecycle(p,'PUBLISHED')}>Undeprecate</button>}
                    {(p.lifecycle==='DEPRECATED'||p.lifecycle==='PUBLISHED')&&<button className="btn btn-ghost btn-sm" onClick={()=>lifecycle(p,'RETIRED')}>Retire</button>}
                    <button className="btn btn-ghost btn-sm" onClick={()=>remove(p)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}
      {editing&&<AccessProductEditor initial={editing==='new'?null:editing} apis={apis} onSave={save} onClose={()=>setEditing(null)}/>}
    </div>
  );
}

// ── organisations, members, apps, credentials ─────────────────────────────

function AccessCredentialRow({cred,products,onChange,showToast,setSecret,confirm}){
  const [adding,setAdding]=useState('');
  const [renaming,setRenaming]=useState(null);   // the new name while the key is being renamed
  const productName=id=>(products.find(p=>p.id===id)||{}).displayName||id;
  const act=async(fn,ok)=>{try{await fn();ok&&showToast&&showToast(ok,'success');await onChange();}catch(e){showToast&&showToast(e.message,'error');}};
  const base='/api/admin/credentials/'+cred.id;
  const expired=cred.expiresAt&&new Date(cred.expiresAt)<new Date();
  return(
    <div style={{borderTop:'1px solid var(--border)',padding:'10px 0'}}>
      <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
        {renaming!==null
          ?<><input className="form-input" style={{maxWidth:260}} aria-label="New name for this key" autoFocus value={renaming}
                    onChange={e=>setRenaming(e.target.value)}
                    onKeyDown={e=>{if(e.key==='Escape')setRenaming(null);
                      if(e.key==='Enter'&&renaming.trim())act(async()=>{await API('PUT',base,{name:renaming});setRenaming(null);},'Key renamed');}}/>
             <button className="btn btn-primary btn-sm" disabled={!renaming.trim()||renaming.trim()===cred.name}
                     onClick={()=>act(async()=>{await API('PUT',base,{name:renaming});setRenaming(null);},'Key renamed')}>Save</button>
             <button className="btn btn-ghost btn-sm" onClick={()=>setRenaming(null)}>Cancel</button></>
          :<><span style={{fontWeight:600}}>{cred.name||cred.clientId}</span>
             <button className="btn btn-ghost btn-sm" title="Rename this key. Its client id, secret and access are unchanged."
                     onClick={()=>setRenaming(cred.name||'')}>Rename</button></>}
        <span className="font-mono text-sm text-muted">{cred.clientId}</span>
        <AccessBadge value={cred.status}/>
        <span className="text-sm text-muted">{cred.environment}</span>
        <span className="text-sm" style={{color:expired?'var(--error)':'var(--text3)'}}>
          {cred.expiresAt?(expired?'expired ':'expires ')+new Date(cred.expiresAt).toISOString().slice(0,10):'never expires'}
        </span>
        {cred.actsOnBehalf&&<span className="badge badge-warning" style={{fontSize:10}}
              title="Internal app: may send X-Axon-On-Behalf-Of to attribute AI calls to a customer">Acts on behalf of customers</span>}
        <div style={{flex:1}}/>
        {cred.status==='APPROVED'&&<button className="btn btn-ghost btn-sm"
          title="Internal apps only. Lets this key name the customer each AI call is for (X-Axon-On-Behalf-Of), so usage, cost and budgets are tracked per customer. Platform admins only."
          onClick={async()=>{
            const on=!cred.actsOnBehalf;
            if(on&&confirm&&!(await confirm('Let "'+(cred.name||cred.clientId)+'" attribute AI calls to any customer with X-Axon-On-Behalf-Of? Only for an internal app\'s key.')))return;
            act(()=>API('PUT',base+'/acts-on-behalf',{enabled:on}),on?'Key may now act on behalf of customers':'On-behalf access removed');}}>
          {cred.actsOnBehalf?'Stop on-behalf':'Allow on-behalf'}</button>}
        {cred.status==='APPROVED'&&<button className="btn btn-ghost btn-sm" onClick={()=>act(async()=>setSecret(await API('POST',base+'/rotate-secret',{})),null)}>Rotate secret</button>}
        {cred.status==='APPROVED'&&<button className="btn btn-ghost btn-sm" onClick={async()=>{
          if(confirm&&!(await confirm('Revoke the key "'+(cred.name||cred.clientId)+'"? Its calls stop within 3 minutes.')))return;
          act(()=>API('POST',base+'/revoke',{reason:'revoked in console'}),'Key revoked');}}>Revoke key</button>}
        <button className="btn btn-ghost btn-sm" style={{color:'var(--danger,#d32f2f)'}} onClick={async()=>{
          const live=cred.status==='APPROVED';
          const msg=(live?'Revoke and delete':'Delete')+' the key "'+(cred.name||cred.clientId)+'" ('+cred.clientId+')?'
            +(live?' Its calls stop within 3 minutes.':'')+' This cannot be undone: a new key gets a new client id and secret.';
          if(confirm?!(await confirm(msg)):!window.confirm(msg))return;
          act(async()=>{if(live)await API('POST',base+'/revoke',{reason:'deleted in console'});await API('DELETE',base);},'Key deleted');}}>Delete</button>
      </div>
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:8,alignItems:'center'}}>
        {(cred.products||[]).map(g=>(
          <span key={g.productId} className="card" style={{display:'inline-flex',gap:6,alignItems:'center',padding:'3px 8px',margin:0}}>
            <span style={{fontSize:12}}>{productName(g.productId)}</span><AccessBadge value={g.status}/>
            {g.status==='PENDING'&&<>
              <button className="btn btn-ghost btn-sm" onClick={()=>act(()=>API('POST',base+'/products/'+g.productId+'/approve',{}),'Approved')}>Approve</button>
              <button className="btn btn-ghost btn-sm" onClick={()=>act(()=>API('POST',base+'/products/'+g.productId+'/reject',{reason:'rejected in console'}),'Rejected')}>Reject</button></>}
            {g.status==='APPROVED'&&<button className="btn btn-ghost btn-sm" onClick={()=>act(()=>API('POST',base+'/products/'+g.productId+'/revoke',{reason:'revoked in console'}),'Access revoked')}>Revoke</button>}
            {(g.status==='REVOKED'||g.status==='REJECTED')&&<button className="btn btn-ghost btn-sm" onClick={()=>act(()=>API('POST',base+'/products/'+g.productId+'/approve',{}),'Approved')}>Re-approve</button>}
          </span>
        ))}
        {cred.status==='APPROVED'&&(
          <span style={{display:'inline-flex',gap:6}}>
            <select className="form-input" style={{height:30,fontSize:12}} value={adding} aria-label="Add API product" onChange={e=>setAdding(e.target.value)}>
              <option value="">Add API product…</option>
              {products.filter(p=>!(cred.products||[]).some(g=>g.productId===p.id)&&(p.lifecycle==='PUBLISHED'||p.lifecycle==='DEPRECATED'))
                .map(p=><option key={p.id} value={p.id}>{p.displayName}</option>)}
            </select>
            {adding&&<button className="btn btn-primary btn-sm" onClick={()=>act(()=>API('POST',base+'/products',{productId:adding}),'API product added').then(()=>setAdding(''))}>Add</button>}
          </span>
        )}
      </div>
    </div>
  );
}

function AccessNewCredential({org,products,onIssued,onClose}){
  const [name,setName]=useState('');
  const [env,setEnv]=useState('sandbox');
  const [days,setDays]=useState(365);
  const [clientId,setClientId]=useState('');
  const [chosen,setChosen]=useState([]);
  const [err,setErr]=useState('');
  const [issuing,setIssuing]=useState(false);   // a double-click issued the key twice
  const live=products.filter(p=>p.lifecycle==='PUBLISHED'||p.lifecycle==='DEPRECATED');
  const issue=async()=>{
    setErr('');
    if(!name.trim()){setErr('Give the key a name, e.g. the system that will use it');return;}
    if(issuing)return;
    setIssuing(true);
    try{onIssued(await API('POST','/api/admin/organizations/'+org.id+'/credentials',{name:name.trim(),environment:env,lifetimeDays:Number(days),productIds:chosen,clientId:clientId.trim()||undefined}));}
    catch(e){setErr(e.message);}
    finally{setIssuing(false);}
  };
  return(
    <AccessModal title={'New key for '+org.displayName} onClose={onClose}>
      {err&&<div className="alert alert-error" style={{marginBottom:12}}>{err}</div>}
      <div className="form-group"><label className="form-label" htmlFor="nc-name">Name</label>
        <input id="nc-name" className="form-input" autoFocus value={name} placeholder="Which system uses it, e.g. Oracle Fusion production"
               onChange={e=>{setName(e.target.value);setErr('');}}/></div>
      <div className="form-group"><label className="form-label" htmlFor="nc-env">Environment</label>
        <input id="nc-env" className="form-input" value={env} onChange={e=>setEnv(e.target.value)}/></div>
      <div className="form-group"><label className="form-label" htmlFor="nc-days">Lifetime (days, 0 = never expires)</label>
        <input id="nc-days" className="form-input" type="number" min="0" value={days} onChange={e=>setDays(e.target.value)}/></div>
      <div className="form-group"><label className="form-label" htmlFor="nc-cid">Axon key client id (optional)</label>
        <input id="nc-cid" className="form-input" value={clientId} placeholder="Generated if empty, e.g. axon-dev-acme-jurist-sandbox"
               onChange={e=>{setClientId(e.target.value);setErr('');}}/>
        <div className="text-sm text-muted">What the caller sends as its user name. Starts with axon-, lowercase and hyphens, and must not exist yet.</div></div>
      <div className="form-label">API products</div>
      {live.length===0&&<div className="text-sm text-muted">No published API products yet.</div>}
      {live.map(p=>(
        <label key={p.id} className="text-sm" style={{display:'flex',gap:6,alignItems:'center',marginBottom:4}}>
          <input type="checkbox" checked={chosen.includes(p.id)} onChange={e=>setChosen(e.target.checked?chosen.concat([p.id]):chosen.filter(x=>x!==p.id))}/>
          {p.displayName} <span className="text-muted">({(p.access||'').toLowerCase()}, {(p.approvalType||'').toLowerCase()} approval)</span>
        </label>
      ))}
      <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:12}}>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={issue} disabled={issuing}>{issuing?'Issuing…':'Issue key'}</button>
      </div>
    </AccessModal>
  );
}

// The TaxLab (Jurist) credential Axon uses to call Jurist. One platform credential serves every
// organisation; an organisation may hold its own instead (an override). Write-only: the API says
// whether a secret is held, never the secret, so the secret field starts empty and leaving it
// empty keeps the stored one.
function TaxlabCredentialEditor({url,t,act,confirm,scope,name,orgs}){
  const [editing,setEditing]=useState(false);
  const [f,setF]=useState({clientId:'',clientSecret:'',baseUrl:''});
  const [testing,setTesting]=useState(false);
  const [copyFrom,setCopyFrom]=useState('');
  const platform=scope==='platform';
  // organisations holding a credential of their own, which the platform one can be copied from
  const sources=(orgs||[]).filter(o=>o.taxlabCredential&&o.taxlabCredential.source==='organisation');
  const own=platform?t.set:t.source==='organisation';
  const open=(fresh)=>{setF(fresh?{clientId:'',clientSecret:'',baseUrl:''}:{clientId:t.clientId||'',clientSecret:'',baseUrl:t.baseUrl||''});setEditing(true);};
  const save=()=>act(async()=>{await API('PUT',url,f);setEditing(false);},platform?'Platform TaxLab credential saved':'Override saved for '+name);
  const test=async()=>{setTesting(true);
    await act(async()=>{const r=await API('POST',url+'/test',{});
      if(!r.success)throw new Error('TaxLab refused it: '+r.result);},'TaxLab accepted the credential');
    setTesting(false);};
  const remove=async()=>{
    const msg=platform
      ?'Remove the platform TaxLab credential? Organisations without an override then fail at the gateway (502) until one is set.'
      :'Remove the override of '+name+'? It then uses the platform TaxLab credential.';
    const ok=confirm?await confirm(msg):window.confirm(msg);
    if(ok)act(()=>API('DELETE',url),platform?'Platform TaxLab credential removed':'Override removed: '+name+' uses the platform credential');
  };
  return(
    <div>
      {!editing&&(
        <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
          {t.set
            ?<><span className="text-muted">TaxLab (Jurist) client id</span><span className="font-mono">{t.clientId}</span>
               {!platform&&<span className={'badge badge-'+(own?'warning':'info')} style={{fontSize:10}}>{own?'override for this organisation':'platform credential'}</span>}
               {t.baseUrl&&<span className="text-muted">{t.baseUrl}</span>}
               {t.lastTestResult&&<span className={'badge badge-'+(t.lastTestResult==='SUCCESS'?'success':'error')} style={{fontSize:10}}
                     title={t.lastTestedAt?'Tested '+new Date(t.lastTestedAt).toLocaleString():''}>{t.lastTestResult==='SUCCESS'?'tested OK':t.lastTestResult}</span>}</>
            :<span className="text-muted">{platform
               ?'Not set: organisations without an override fail at the gateway (502) until it is.'
               :'None applies: there is no platform credential and no override, so REST calls fail at the gateway (502).'}</span>}
          <div style={{flex:1}}/>
          {t.set&&<button className="btn btn-ghost btn-sm" disabled={testing} onClick={test}>{testing?'Testing…':'Test'}</button>}
          {platform
            ?<><button className="btn btn-ghost btn-sm" onClick={()=>open(false)}>{t.set?'Change':'Set credential'}</button>
               {t.set&&<button className="btn btn-ghost btn-sm" onClick={remove}>Remove</button>}</>
            :own
              ?<><button className="btn btn-ghost btn-sm" onClick={()=>open(false)}>Change override</button>
                 <button className="btn btn-ghost btn-sm" onClick={remove}>Use platform credential</button></>
              :<button className="btn btn-ghost btn-sm" onClick={()=>open(true)}>Override for this organisation</button>}
        </div>
      )}
      {editing&&(
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:10,alignItems:'end',maxWidth:820}}>
          <div className="form-group" style={{margin:0}}><label className="form-label" htmlFor={scope+'-tl-id'}>TaxLab (Jurist) client id</label>
            <input id={scope+'-tl-id'} className="form-input font-mono" value={f.clientId} onChange={e=>setF(Object.assign({},f,{clientId:e.target.value}))}/></div>
          <div className="form-group" style={{margin:0}}><label className="form-label" htmlFor={scope+'-tl-secret'}>TaxLab (Jurist) client secret</label>
            <input id={scope+'-tl-secret'} type="password" autoComplete="new-password" className="form-input" value={f.clientSecret}
                   placeholder={own&&t.secretSet?'Leave empty to keep the current one':''}
                   onChange={e=>setF(Object.assign({},f,{clientSecret:e.target.value}))}/></div>
          <div className="form-group" style={{margin:0}}><label className="form-label" htmlFor={scope+'-tl-base'}>Base URL (optional)</label>
            <input id={scope+'-tl-base'} className="form-input font-mono" value={f.baseUrl} placeholder="https://sandbox-api.taxlab.ai"
                   onChange={e=>setF(Object.assign({},f,{baseUrl:e.target.value}))}/></div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary btn-sm" onClick={save}>Save</button>
            <button className="btn btn-ghost btn-sm" onClick={()=>setEditing(false)}>Cancel</button>
          </div>
          {platform&&sources.length>0&&(
            <div style={{gridColumn:'1 / -1',display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
              <span className="text-sm text-muted">or copy an organisation's credential (the secret is copied on the server, never shown):</span>
              <select className="form-input" style={{maxWidth:280}} aria-label="Organisation to copy from" value={copyFrom} onChange={e=>setCopyFrom(e.target.value)}>
                <option value="">Choose an organisation…</option>
                {sources.map(o=><option key={o.id} value={o.id}>{o.displayName} ({o.taxlabCredential.clientId})</option>)}
              </select>
              <button className="btn btn-ghost btn-sm" disabled={!copyFrom}
                      onClick={()=>act(async()=>{await API('PUT',url,{copyFromOrganization:copyFrom});setEditing(false);setCopyFrom('');},'Platform TaxLab credential copied')}>Copy</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// What Jurist is told about the customer on every call (the signed X-Axon-Caller): the entity
// name and the TaxLab account id, from this record only -- never from the request.
function AccessCustomerIdentity({org,base,act}){
  const acct=(org.attributes||{}).taxlabAccount||'';
  const [editing,setEditing]=useState(false);
  const [f,setF]=useState({name:org.displayName||'',account:acct});
  const save=()=>act(async()=>{
    const attrs=Object.assign({},org.attributes||{});
    if(f.account.trim())attrs.taxlabAccount=f.account.trim().toUpperCase();else delete attrs.taxlabAccount;
    await API('PUT',base,{displayName:f.name.trim(),attributes:attrs});setEditing(false);},'Customer identity saved');
  return(
    <div style={{marginTop:14}}>
      <h4 style={{margin:'0 0 6px'}}>Customer identity <span className="text-muted" style={{fontWeight:400,fontSize:12}}>sent to Jurist on every call, signed by Axon (X-Axon-Caller)</span></h4>
      {!editing
        ?<div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <span className="text-muted">Entity name</span><span>{org.displayName}</span>
            <span className="text-muted">Account id</span>{acct?<span className="font-mono">{acct}</span>:<span className="text-muted">not set: Jurist gets the entity name only</span>}
            <div style={{flex:1}}/>
            <button className="btn btn-ghost btn-sm" onClick={()=>{setF({name:org.displayName||'',account:acct});setEditing(true);}}>Edit</button>
          </div>
        :<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:10,alignItems:'end',maxWidth:720}}>
            <div className="form-group" style={{margin:0}}><label className="form-label" htmlFor="ci-name">Entity name</label>
              <input id="ci-name" className="form-input" value={f.name} onChange={e=>setF(Object.assign({},f,{name:e.target.value}))}/></div>
            <div className="form-group" style={{margin:0}}><label className="form-label" htmlFor="ci-acct">Account id (TaxLab account number)</label>
              <input id="ci-acct" className="form-input font-mono" value={f.account} placeholder="e.g. 01M0ACS35R0J4BZDB4G7VPBVFA" onChange={e=>setF(Object.assign({},f,{account:e.target.value}))}/></div>
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary btn-sm" disabled={!f.name.trim()} onClick={save}>Save</button>
              <button className="btn btn-ghost btn-sm" onClick={()=>setEditing(false)}>Cancel</button>
            </div>
          </div>}
    </div>
  );
}

function AccessTaxlabCredential({org,base,act,confirm}){
  return(
    <div style={{marginTop:14}}>
      <h4 style={{margin:'0 0 6px'}}>TaxLab (Jurist) credential</h4>
      <TaxlabCredentialEditor url={base+'/taxlab-credential'} t={org.taxlabCredential||{}} act={act} confirm={confirm} scope="organisation" name={org.displayName}/>
    </div>
  );
}

// The platform credential, above the organisation list. Only platform-admin may read it, so for
// anyone else the card simply does not render.
function AccessPlatformTaxlab({showToast,confirm,onChange,orgs}){
  const [t,setT]=useState(null);
  const load=useCallback(()=>API('GET','/api/admin/taxlab-credential/platform').then(setT).catch(()=>setT(false)),[]);
  useEffect(()=>{load();},[load]);
  const act=async(fn,ok)=>{try{await fn();ok&&showToast&&showToast(ok,'success');await load();onChange&&onChange();}catch(e){showToast&&showToast(e.message,'error');}};
  if(!t)return null;
  return(
    <div className="card" style={{marginBottom:16}}><div className="card-body">
      <div style={{display:'flex',gap:10,alignItems:'baseline',flexWrap:'wrap',marginBottom:8}}>
        <h4 style={{margin:0}}>Platform TaxLab (Jurist) credential</h4>
        <span className="text-sm text-muted">Axon calls Jurist with this for every organisation that has no override. Customers never see it.</span>
      </div>
      <TaxlabCredentialEditor url="/api/admin/taxlab-credential/platform" t={t} act={act} confirm={confirm} scope="platform" name="Platform" orgs={orgs}/>
    </div></div>
  );
}

function AccessOrgDetail({orgId,products,showToast,confirm,onListChange,onDelete}){
  const [d,setD]=useState(null);
  const [secret,setSecret]=useState(null);
  const [newCredFor,setNewCredFor]=useState(null);
  const [member,setMember]=useState({email:'',role:'DEVELOPER'});
  const load=useCallback(async()=>{
    try{setD(await API('GET','/api/admin/organizations/'+orgId));}catch(e){showToast&&showToast(e.message,'error');}
  },[orgId,showToast]);
  useEffect(()=>{load();},[load]);
  if(!d)return <div className="card"><div className="card-body">Loading…</div></div>;
  const o=d.organization;
  const base='/api/admin/organizations/'+o.id;
  const act=async(fn,ok)=>{try{await fn();ok&&showToast&&showToast(ok,'success');await load();onListChange&&onListChange();}catch(e){showToast&&showToast(e.message,'error');}};

  return(
    <div className="card" style={{marginTop:16}}><div className="card-body">
      <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
        <div style={{fontWeight:600,fontSize:16}}>{o.displayName}</div>
        <span className="font-mono text-muted">{o.alias}</span>
        <AccessBadge value={o.status}/>
        {o.keycloakOrgId&&<span className="badge badge-info" style={{fontSize:10}}>Keycloak organisation</span>}
        <div style={{flex:1}}/>
        <button className="btn btn-ghost btn-sm" onClick={()=>act(()=>API('PUT',base+'/status',{status:o.status==='ACTIVE'?'INACTIVE':'ACTIVE'}),
          o.status==='ACTIVE'?'Organisation deactivated: its calls stop within 3 minutes':'Organisation activated')}>
          {o.status==='ACTIVE'?'Deactivate':'Activate'}</button>
        <button className="btn btn-ghost btn-sm" style={{color:'var(--danger,#d32f2f)'}} title="Delete this organisation and its keys"
                onClick={()=>onDelete&&onDelete(o)}>Delete organisation</button>
      </div>
      <AccessCustomerIdentity org={o} base={base} act={act}/>
      <AccessTaxlabCredential org={o} base={base} act={act} confirm={confirm}/>

      <h4 style={{margin:'18px 0 6px'}}>Members</h4>
      <table className="table" style={{width:'100%'}}><tbody>
        {d.members.length===0&&<tr><td className="text-muted">No members yet.</td></tr>}
        {d.members.map(m=>(
          <tr key={m.id}>
            <td>{m.email}{m.username&&<span className="text-muted"> ({m.username})</span>}{!m.userSub&&<span className="text-muted"> — not signed in yet</span>}</td>
            <td style={{width:180}}><select className="form-input" value={m.role} aria-label="Role"
                    onChange={e=>act(()=>API('PUT',base+'/members/'+m.id,{role:e.target.value}),'Role changed')}>
              <option value="ADMIN">Org admin</option><option value="DEVELOPER">Developer</option></select></td>
            <td style={{width:90,textAlign:'right'}}><button className="btn btn-ghost btn-sm" onClick={()=>act(()=>API('DELETE',base+'/members/'+m.id),'Member removed')}>Remove</button></td>
          </tr>
        ))}
      </tbody></table>
      <div style={{display:'flex',gap:8,marginTop:8,flexWrap:'wrap'}}>
        <input className="form-input" style={{maxWidth:280}} placeholder="name@company.com" aria-label="Member email" value={member.email}
               onChange={e=>setMember(Object.assign({},member,{email:e.target.value}))}/>
        <select className="form-input" style={{width:150}} aria-label="Member role" value={member.role} onChange={e=>setMember(Object.assign({},member,{role:e.target.value}))}>
          <option value="DEVELOPER">Developer</option><option value="ADMIN">Org admin</option></select>
        <button className="btn btn-ghost btn-sm" disabled={!member.email} onClick={()=>act(()=>API('POST',base+'/members',member),'Member added').then(()=>setMember({email:'',role:'DEVELOPER'}))}>Add member</button>
      </div>

      <div style={{display:'flex',alignItems:'center',gap:10,margin:'18px 0 6px'}}>
        <h4 style={{margin:0}}>Keys</h4>
        <span className="text-sm text-muted">one per system that calls the API; the product quota is shared by all of them</span>
        <div style={{flex:1}}/>
        {o.status==='ACTIVE'&&<button className="btn btn-primary btn-sm" onClick={()=>setNewCredFor(o)}>New key</button>}
      </div>
      {d.credentials.length===0&&<div className="text-sm text-muted">No keys yet.</div>}
      {d.credentials.map(c=><AccessCredentialRow key={c.id} cred={c} products={products} onChange={async()=>{await load();onListChange&&onListChange();}} showToast={showToast} setSecret={setSecret} confirm={confirm}/>)}
      {newCredFor&&<AccessNewCredential org={newCredFor} products={products} onClose={()=>setNewCredFor(null)}
                                       onIssued={async(s)=>{setNewCredFor(null);setSecret(s);await load();}}/>}
      <AccessSecretModal payload={secret} onClose={()=>setSecret(null)}/>
    </div></div>
  );
}

function AccessOrgsPage({showToast,confirm}){
  const [orgs,setOrgs]=useState([]);
  const [products,setProducts]=useState([]);
  const [pending,setPending]=useState({});
  const [selected,setSelected]=useState(null);
  const [creating,setCreating]=useState(false);
  const EMPTY_ORG={alias:'',displayName:''};
  const [form,setForm]=useState(EMPTY_ORG);
  const [err,setErr]=useState('');
  const reload=useCallback(async()=>{
    try{
      const [o,p,q]=await Promise.all([API('GET','/api/admin/organizations'),API('GET','/api/admin/products'),
        API('GET','/api/admin/credentials/pending').catch(()=>[])]);
      setOrgs(Array.isArray(o)?o:[]);setProducts(Array.isArray(p)?p:[]);
      const n={};(Array.isArray(q)?q:[]).forEach(r=>{n[r.organizationId]=(n[r.organizationId]||0)+1;});setPending(n);
    }catch(e){showToast&&showToast('Could not load organisations: '+e.message,'error');}
  },[showToast]);
  useEffect(()=>{reload();},[reload]);
  const create=async()=>{
    setErr('');
    try{const o=await API('POST','/api/admin/organizations',form);setCreating(false);setForm(EMPTY_ORG);
      showToast&&showToast('Organisation created','success');await reload();setSelected(o.id);}
    catch(e){setErr(e.message);}
  };
  // Delete, from the list or the detail. An active organisation is deactivated first (the
  // server deletes only inactive ones), after the alias is typed to confirm.
  const deleteOrg=async(o)=>{
    const typed=window.prompt('Delete "'+o.displayName+'" for good?\n\n'
      +(o.status==='ACTIVE'?'It is active: it is deactivated first, so its keys stop working.\n\n':'')
      +'Its keys, members, TaxLab override and any legacy subscriptions are removed. Keycloak clients Axon created for its keys are deleted; shared legacy clients are left in Keycloak.\n\n'
      +'Type the alias to confirm: '+o.alias);
    if(typed===null)return;
    if(typed.trim()!==o.alias){showToast&&showToast('The alias did not match; nothing was deleted','error');return;}
    try{
      if(o.status==='ACTIVE')await API('PUT','/api/admin/organizations/'+o.id+'/status',{status:'INACTIVE'});
      const r=await API('DELETE','/api/admin/organizations/'+o.id+'?confirm='+encodeURIComponent(o.alias));
      showToast&&showToast('Deleted '+r.deleted+': '+r.keysDeleted+' key(s)'
        +((r.keycloakClientsKept||[]).length?'; left in Keycloak: '+r.keycloakClientsKept.join(', '):''),'success');
      if(selected===o.id)setSelected(null);
    }catch(e){showToast&&showToast(e.message,'error');}
    await reload();
  };
  return(
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Organisations</h1>
          <div className="page-subtitle">Customer companies: their members, and keys with the API products each key holds. Axon reaches Jurist with the platform TaxLab credential unless an organisation overrides it.</div></div>
        <button className="btn btn-primary" onClick={()=>setCreating(true)}>New organisation</button>
      </div>
      <AccessPlatformTaxlab showToast={showToast} confirm={confirm} onChange={reload} orgs={orgs}/>
      <div className="card"><div className="card-body" style={{padding:0,overflowX:'auto'}}>
        <table className="table" style={{width:'100%'}}>
          <thead><tr><th>Organisation</th><th>Alias</th><th>Status</th><th>TaxLab (Jurist) credential</th><th>Requests</th><th></th></tr></thead>
          <tbody>
            {orgs.length===0&&<tr><td colSpan={6} className="text-muted">No organisations yet.</td></tr>}
            {orgs.map(o=>(
              <tr key={o.id} onClick={()=>setSelected(selected===o.id?null:o.id)} style={{cursor:'pointer',background:selected===o.id?'var(--surface2)':undefined}}>
                <td style={{fontWeight:600}}>{o.displayName}</td>
                <td className="font-mono">{o.alias}</td>
                <td><AccessBadge value={o.status}/></td>
                <td>{o.taxlabCredential&&o.taxlabCredential.set
                  ?<><span className="font-mono">{o.taxlabCredential.clientId}</span> <span className="text-muted text-sm">{o.taxlabCredential.source==='organisation'?'(override)':'(platform)'}</span></>
                  :<span className="text-muted">none</span>}</td>
                <td>{pending[o.id]?<span className="badge badge-warning" style={{fontSize:10}} title="API products requested on this organisation's credentials, waiting for approval">{pending[o.id]} pending</span>:<span className="text-muted">none</span>}</td>
                <td style={{width:80,textAlign:'right'}}><button className="btn btn-ghost btn-sm" style={{color:'var(--danger,#d32f2f)'}}
                    onClick={e=>{e.stopPropagation();deleteOrg(o);}}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div></div>
      {selected&&<AccessOrgDetail key={selected} orgId={selected} products={products} showToast={showToast} confirm={confirm} onListChange={reload} onDelete={deleteOrg}/>}
      {creating&&(
        <AccessModal title="New organisation" onClose={()=>setCreating(false)}>
          {err&&<div className="alert alert-error" style={{marginBottom:12}}>{err}</div>}
          <div className="form-group"><label className="form-label" htmlFor="no-name">Name</label>
            <input id="no-name" className="form-input" value={form.displayName} placeholder="Al Batha Trading"
                   onChange={e=>{const n=e.target.value;setForm(f=>Object.assign({},f,{displayName:n,alias:f.alias||''}));}}/></div>
          <div className="form-group"><label className="form-label" htmlFor="no-alias">Alias</label>
            <input id="no-alias" className="form-input font-mono" value={form.alias}
                   placeholder={(form.displayName||'').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')}
                   onChange={e=>setForm(Object.assign({},form,{alias:e.target.value.toLowerCase()}))}/></div>
          <div className="text-sm text-muted" style={{marginBottom:12}}>It reaches Jurist with the platform TaxLab credential. To give it its own,
            open it after creating it and choose "Override for this organisation".</div>
          <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
            <button className="btn btn-ghost" onClick={()=>setCreating(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={()=>{if(!form.alias)form.alias=(form.displayName||'').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');create();}}>Create</button>
          </div>
        </AccessModal>
      )}
    </div>
  );
}

// ── the customer developer portal (axon-portal sessions) ───────────────────
// Rendered instead of the console when the session's token was issued to axon-portal
// (isCustomerSession in app.js). Every call goes to /api/portal/my/**, where the server
// works out the caller's organisations from the token; nothing here sends an org id the
// server has not first listed as the caller's own.

function CustomerPortalApp({user,onLogout,showToast}){
  const [me,setMe]=useState(null);
  const [orgId,setOrgId]=useState(null);
  const [view,setView]=useState('catalog');
  const [catalog,setCatalog]=useState([]);
  useEffect(()=>{
    API('GET','/api/portal/my').then(m=>{setMe(m);if(m.organizations&&m.organizations.length)setOrgId(m.organizations[0].id);})
      .catch(e=>showToast&&showToast(e.message,'error'));
    API('GET','/api/portal/catalog').then(c=>setCatalog(Array.isArray(c)?c:[])).catch(()=>setCatalog([]));
  },[showToast]);
  const org=me&&me.organizations?me.organizations.find(o=>o.id===orgId):null;
  const isAdmin=!!org&&org.role==='ADMIN';
  return(
    <div style={{minHeight:'100vh',background:'var(--bg)'}}>
      <div style={{display:'flex',alignItems:'center',gap:14,padding:'12px 24px',borderBottom:'1px solid var(--border)',background:'var(--surface)',flexWrap:'wrap'}}>
        <img src="logo.png" alt="TaxLab" style={{height:26}}/>
        <div style={{fontWeight:600}}>Developer Portal</div>
        <div style={{display:'flex',gap:4,marginLeft:16}}>
          {[['catalog','API products'],['keys','Keys'],['usage','Usage']].map(([k,l])=>(
            <button key={k} className={'um-tab'+(view===k?' active':'')} onClick={()=>setView(k)}>{l}</button>
          ))}
        </div>
        <div style={{flex:1}}/>
        {me&&me.organizations&&me.organizations.length>1&&(
          <select className="form-input" style={{width:220}} aria-label="Organisation" value={orgId||''} onChange={e=>setOrgId(e.target.value)}>
            {me.organizations.map(o=><option key={o.id} value={o.id}>{o.displayName}</option>)}
          </select>
        )}
        {org&&<span className="text-sm text-muted">{org.displayName} · {org.role==='ADMIN'?'org admin':'developer'}</span>}
        <span className="text-sm">{(me&&me.email)||(user&&user.email)||''}</span>
        <button className="btn btn-ghost btn-sm" onClick={onLogout}>Sign out</button>
      </div>
      <div style={{maxWidth:1100,margin:'0 auto',padding:'24px 16px'}}>
        {me&&(!me.organizations||me.organizations.length===0)&&(
          <div className="card" style={{marginBottom:16}}><div className="card-body">
            <h3 style={{marginTop:0}}>Your account is not part of an organisation yet</h3>
            <p className="text-muted" style={{marginBottom:0}}>Ask your TaxLab contact to add <b>{me.email||me.username}</b> to your company's organisation. You can browse the API products meanwhile.</p>
          </div></div>
        )}
        {view==='catalog'&&<CustomerCatalog catalog={catalog}/>}
        {view==='keys'&&org&&<CustomerKeys org={org} isAdmin={isAdmin} catalog={catalog} showToast={showToast}/>}
        {view==='usage'&&org&&<CustomerUsage org={org} showToast={showToast}/>}
      </div>
    </div>
  );
}

function CustomerCatalog({catalog}){
  if(!catalog.length)return <div className="card"><div className="card-body text-muted">No API products are published yet.</div></div>;
  return(
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:16}}>
      {catalog.map(p=>(
        <div key={p.id} className="card"><div className="card-body">
          <div style={{display:'flex',justifyContent:'space-between',gap:8,alignItems:'flex-start'}}>
            <div style={{fontWeight:600,fontSize:15}}>{p.displayName}</div><AccessBadge value={p.lifecycle}/>
          </div>
          {p.description&&<p className="text-sm text-muted">{p.description}</p>}
          <div className="text-sm" style={{marginTop:8}}>{accessQuotaText(p.quota)} · {p.approvalType==='AUTO'?'instant access':'access approved by TaxLab'}</div>
          <div style={{marginTop:10}}>
            {(p.apis||[]).map(a=>(
              <div key={a.id} className="text-sm" style={{display:'flex',justifyContent:'space-between',gap:8,padding:'4px 0',borderTop:'1px solid var(--border)'}}>
                <span>{a.name} <span className="text-muted">{a.version} · {a.operations} operations</span></span>
                <a href={a.openapi} target="_blank" rel="noreferrer">OpenAPI</a>
              </div>
            ))}
          </div>
        </div></div>
      ))}
    </div>
  );
}

function CustomerKeys({org,isAdmin,catalog,showToast}){
  const [keys,setKeys]=useState(null);
  const [secret,setSecret]=useState(null);
  const [issuing,setIssuing]=useState(false);
  const [name,setName]=useState('');
  const [chosen,setChosen]=useState([]);
  const [err,setErr]=useState('');
  const [busy,setBusy]=useState(false);   // a double-click issued the key twice
  const load=useCallback(()=>API('GET','/api/portal/my/orgs/'+org.id+'/credentials').then(setKeys)
    .catch(e=>{setKeys([]);showToast&&showToast(e.message,'error');}),[org.id,showToast]);
  useEffect(()=>{load();},[load]);
  const act=async(fn,ok)=>{try{const r=await fn();ok&&showToast&&showToast(ok,'success');await load();return r;}catch(e){showToast&&showToast(e.message,'error');return null;}};
  const pname=id=>(catalog.find(p=>p.id===id)||{}).displayName||id;
  const issue=async()=>{
    if(!name.trim()){setErr('Give the key a name, e.g. the system that will use it');return;}
    if(busy)return;
    setBusy(true);
    try{const r=await API('POST','/api/portal/my/orgs/'+org.id+'/credentials',{name:name.trim(),environment:'sandbox',productIds:chosen});
      setIssuing(false);setSecret(r);await load();}
    catch(e){setErr(e.message);}
    finally{setBusy(false);}
  };
  return(
    <div>
      {isAdmin&&org.status==='ACTIVE'&&(
        <div style={{marginBottom:16}}>
          <button className="btn btn-primary" onClick={()=>{setIssuing(true);setName('');setChosen([]);setErr('');}}>New key</button>
        </div>
      )}
      {keys===null&&<div className="card"><div className="card-body">Loading…</div></div>}
      {keys&&keys.length===0&&<div className="card"><div className="card-body text-muted">No keys yet.{isAdmin?' Create one for each system that will call the API.':' An org admin can create one.'}</div></div>}
      {(keys||[]).map(c=>(
        <div key={c.id} className="card" style={{marginBottom:12}}><div className="card-body">
          <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontWeight:600}}>{c.name||c.clientId}</span>
            <span className="font-mono text-sm text-muted">{c.clientId}</span><AccessBadge value={c.status}/>
            <span className="text-sm text-muted">{c.environment} · {c.expiresAt?'expires '+String(c.expiresAt).slice(0,10):'no expiry'}</span>
            <div style={{flex:1}}/>
            {isAdmin&&c.status==='APPROVED'&&<button className="btn btn-ghost btn-sm" onClick={()=>act(()=>API('POST','/api/portal/my/credentials/'+c.id+'/rotate-secret',{})).then(r=>{if(r)setSecret(r);})}>Rotate secret</button>}
          </div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:6}}>
            {(c.products||[]).map(g=><span key={g.productId} className="text-sm" style={{display:'inline-flex',gap:6,alignItems:'center'}}>{pname(g.productId)} <AccessBadge value={g.status}/></span>)}
            {isAdmin&&c.status==='APPROVED'&&catalog.filter(p=>!(c.products||[]).some(g=>g.productId===p.id)).map(p=>(
              <button key={p.id} className="btn btn-ghost btn-sm" onClick={()=>act(()=>API('POST','/api/portal/my/credentials/'+c.id+'/products',{productId:p.id}),
                p.approvalType==='AUTO'?'Access granted':'Access requested; TaxLab will review it')}>Request {p.displayName}</button>
            ))}
          </div>
        </div></div>
      ))}
      {issuing&&(
        <AccessModal title={'New key for '+org.displayName} onClose={()=>setIssuing(false)}>
          {err&&<div className="alert alert-error" style={{marginBottom:12}}>{err}</div>}
          <div className="form-group"><label className="form-label" htmlFor="ck-name">Name</label>
            <input id="ck-name" className="form-input" autoFocus value={name} placeholder="Which system uses it, e.g. Oracle Fusion production"
                   onChange={e=>{setName(e.target.value);setErr('');}}/></div>
          <p className="text-sm text-muted" style={{marginTop:0}}>The key is valid for one year. Choose the API products it should reach.</p>
          {catalog.map(p=>(
            <label key={p.id} className="text-sm" style={{display:'flex',gap:6,alignItems:'center',marginBottom:4}}>
              <input type="checkbox" checked={chosen.includes(p.id)} onChange={e=>setChosen(e.target.checked?chosen.concat([p.id]):chosen.filter(x=>x!==p.id))}/>
              {p.displayName} <span className="text-muted">({p.approvalType==='AUTO'?'instant':'needs approval'})</span>
            </label>
          ))}
          <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:12}}>
            <button className="btn btn-ghost" onClick={()=>setIssuing(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={issue} disabled={busy}>{busy?'Issuing…':'Issue key'}</button>
          </div>
        </AccessModal>
      )}
      <AccessSecretModal payload={secret} onClose={()=>setSecret(null)}/>
    </div>
  );
}

// "app" is the usage records' column for the key name (it predates keys replacing apps).
const CUSTOMER_USAGE_GROUPS=[['app','Key'],['product','API product'],['api','API'],['status','Status'],['day','Day']];

function CustomerUsage({org,showToast}){
  const [groupBy,setGroupBy]=useState('app');
  const [days,setDays]=useState(7);
  const [rows,setRows]=useState(null);
  useEffect(()=>{
    setRows(null);
    API('GET','/api/portal/my/orgs/'+org.id+'/usage?groupBy='+groupBy+'&days='+days).then(r=>setRows(Array.isArray(r)?r:[]))
      .catch(e=>{setRows([]);showToast&&showToast(e.message,'error');});
  },[org.id,groupBy,days,showToast]);
  return(
    <div>
      <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap'}}>
        {CUSTOMER_USAGE_GROUPS.map(([k,l])=>(
          <button key={k} className={'um-tab'+(groupBy===k?' active':'')} onClick={()=>setGroupBy(k)}>{l}</button>
        ))}
        <select className="form-input" style={{width:150,marginLeft:'auto'}} aria-label="Period" value={days} onChange={e=>setDays(Number(e.target.value))}>
          <option value={1}>Last 24 hours</option><option value={7}>Last 7 days</option><option value={30}>Last 30 days</option>
        </select>
      </div>
      <div className="card"><div className="card-body" style={{padding:0,overflowX:'auto'}}>
        <table className="table" style={{width:'100%',fontVariantNumeric:'tabular-nums'}}>
          <thead><tr><th>{(CUSTOMER_USAGE_GROUPS.find(g=>g[0]===groupBy)||[0,groupBy])[1]}</th><th style={{textAlign:'right'}}>Calls</th><th style={{textAlign:'right'}}>Errors</th><th style={{textAlign:'right'}}>Throttled</th><th style={{textAlign:'right'}}>Avg ms</th></tr></thead>
          <tbody>
            {rows===null&&<tr><td colSpan={5}>Loading…</td></tr>}
            {rows&&rows.length===0&&<tr><td colSpan={5} className="text-muted">No calls in this period.</td></tr>}
            {(rows||[]).map(r=>(
              <tr key={String(r.bucket)}><td>{r.bucket||'—'}</td><td style={{textAlign:'right'}}>{r.calls.toLocaleString()}</td>
                <td style={{textAlign:'right'}}>{r.errors.toLocaleString()}</td><td style={{textAlign:'right'}}>{r.throttled.toLocaleString()}</td>
                <td style={{textAlign:'right'}}>{r.avgMs}</td></tr>
            ))}
          </tbody>
        </table>
      </div></div>
    </div>
  );
}
